import React, { Component } from 'react';
import { StripeProvider, Elements } from "react-stripe-elements";
import App_data from '../config/App_data';
import FD from '../RestAPI/fetchData';
import Loader from '../../common/loader';
import "../../assets/css/subscribe.css";
import { showFailure, showInformation } from '../../common/toast';
import { formatMoney, hasSubscriptionRegularMix, isSubscriptionItem } from '../../utils/CommonUtils';
import userStore from "../../store/userStore";
import StripeCheckoutForm from "../../components/CheckoutComponents/PaymentMethods/StripeCheckoutForm";
import ExpressCheckoutWallets from "../../components/CheckoutComponents/PaymentMethods/ExpressCheckoutWallets";
import PayPalButton from "../../components/CheckoutComponents/PaymentMethods/PayPalButton";
import { trackAddPaymentInfo, trackPaymentFailed } from '../../common/analytics';

/* eslint-disable*/

class Checkout extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: true,
      errorMessage: "",
      cartItems: [],

      paymentMethod: "",
      // paymentMethod: "card",
      // paymentMethod: "paypal",
      isUserAuthorized: false,
      profileInfo: null,
      isProcessingPayment: false,
      stripe_key: null,
      stripe: null,
      paypal_client_id: null,
      paypal_env: null,
    };

    this.stripeFormRef = null;
    this.handleInputChange = this.handleInputChange.bind(this);
    this.onStripeFormRef = this.onStripeFormRef.bind(this);
    this.handlePayment = this.handlePayment.bind(this);
  }

  componentDidMount() {
    this.getIsUserAuthorized();
    this.loadUserCheckout();
    this.fetchPaymentCredentials();
    this.loadStripeJs();
    // Soft timeout — if Stripe.js + key haven't both arrived within 15s,
    // surface an error instead of leaving the user on an infinite spinner.
    this._stripeReadyTimeout = setTimeout(() => {
      if (!this.state.stripe && !this.state.errorMessage) {
        this.setState({
          errorMessage: "Payment form is taking too long to load. Please refresh and try again.",
        });
      }
    }, 15000);
  }

  componentWillUnmount() {
    if (this._stripeReadyTimeout) {
      clearTimeout(this._stripeReadyTimeout);
      this._stripeReadyTimeout = null;
    }
  }

  loadStripeJs() {
    if (window.Stripe) {
      this.initStripe();
      return;
    }
    var script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => {
      this.initStripe();
    };
    script.onerror = () => {
      // Stripe.js blocked by ad-blocker, CSP, or network failure.
      // Without this handler the second <Loader/> would spin forever.
      this.setState({
        errorMessage: "Couldn't load the payment form. Please disable ad blockers or check your connection, then refresh.",
      });
    };
    document.body.appendChild(script);
  }

  initStripe() {
    var key = this.state.stripe_key;
    if (!key || !window.Stripe) return;
    // Always create a fresh Stripe instance bound to the current key,
    // so a later key update (e.g. from getPaymentCred) replaces any
    // stale instance that may have been created with a build-time key.
    this.setState({ stripe: window.Stripe(key) }, () => {
      // Stripe is ready — cancel the soft timeout so it can't fire later
      // and overwrite a working state with a stale "taking too long" error.
      if (this._stripeReadyTimeout) {
        clearTimeout(this._stripeReadyTimeout);
        this._stripeReadyTimeout = null;
      }
    });
  }

  onStripeFormRef(ref) {
    this.stripeFormRef = ref;
  }

  async fetchPaymentCredentials() {
    try {
      const resp = await FD.callAPI(App_data.backend_api + '/checkout_v1/getPaymentCred');
      if (resp && resp.success) {
        // PayPal — fall back to the legacy `paypal_app_id` name while
        // any cached backend still serves it.
        if (resp.paypal_client_id || resp.paypal_app_id) {
          this.setState({
            paypal_client_id: resp.paypal_client_id || resp.paypal_app_id,
            paypal_env: resp.paypal_env || 'sandbox',
          });
        }
        if (resp.stripe_key) {
          this.setState({ stripe_key: resp.stripe_key }, () => {
            if (window.Stripe) {
              this.initStripe();
            }
          });
        } else if (App_data.stripe_key) {
          // Backend responded OK but carried no Stripe key — use the build-time
          // key if one is configured (defense in depth).
          this.setState({ stripe_key: App_data.stripe_key }, () => {
            if (window.Stripe) this.initStripe();
          });
        } else {
          // Backend reachable but returned no Stripe key, AND no build-time key
          // is set (REACT_APP_STRIPEKEY is empty in this build). Without this
          // guard the page would hang on the second <Loader/> forever, since
          // `stripe` never gets created. Surface a clear, actionable error.
          this.setState({
            errorMessage: "Payment is temporarily unavailable (no payment key configured). Please refresh, and contact support if the problem persists.",
          });
        }
      }
    } catch (err) {
      // Fallback to build-time key only if backend call fails.
      if (!this.state.stripe_key && App_data.stripe_key) {
        this.setState({ stripe_key: App_data.stripe_key }, () => {
          if (window.Stripe) this.initStripe();
        });
        return;
      }
      // No fallback available — surface the failure so the user is not
      // stuck on the second <Loader/> with no signal of what went wrong.
      this.setState({
        errorMessage: "Couldn't reach the payment service. Please refresh and try again.",
      });
    }
  }

  /**
   * Build the common analytics payload shared by add_payment_info and
   * payment_failed events. Reads from this.state + userStore. Wrapped at
   * call sites with try/catch — never throws.
   */
  buildAnalyticsContext() {
    const { cartItems } = this.state;
    let total = 0;
    try {
      const totals = this.getCartTotals();
      total = (totals && totals.total) || 0;
    } catch (e) { /* ignore */ }
    const profile = (userStore && userStore.profileInfo) || (this.state.profileInfo || {});
    return {
      cartItems: Array.isArray(cartItems) ? cartItems : [],
      total,
      paymentType: 'stripe',
      userName: profile.username || '',
      userEmail: profile.email || '',
      source: (userStore && userStore.landingSource) || 'organic',
      affiliateName: (userStore && userStore.landingSourceName) || null,
    };
  }

  /** Safe wrapper — never throws into the payment flow. */
  fireAddPaymentInfoSafe() {
    try {
      trackAddPaymentInfo(this.buildAnalyticsContext());
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Checkout add_payment_info tracking failed:', e && e.message);
    }
  }

  /** Safe wrapper — never throws into the payment flow. */
  firePaymentFailedSafe(reason, errorMessage) {
    try {
      trackPaymentFailed({
        ...this.buildAnalyticsContext(),
        reason: reason || 'unknown',
        errorMessage: errorMessage || '',
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Checkout payment_failed tracking failed:', e && e.message);
    }
  }

  async handlePayment() {
    if (this.state.isProcessingPayment) return;

    const { cartItems } = this.state;
    if (!cartItems || cartItems.length === 0) {
      showFailure("Your cart is empty.");
      return;
    }

    if (hasSubscriptionRegularMix(cartItems)) {
      showFailure(
        "Subscription and regular products cannot be purchased together. Please check out the subscription separately."
      );
      return;
    }

    if (!this.stripeFormRef) {
      showFailure("Payment form is not ready. Please try again.");
      return;
    }

    this.setState({ isProcessingPayment: true });

    try {
      const { token, error } = await this.stripeFormRef.handleSubmit();

      if (error) {
        // Analytics: card validation failure (Stripe.js token creation rejected).
        this.firePaymentFailedSafe('card_validation', (error && error.message) || 'Card validation failed.');
        showFailure(error.message || "Card validation failed.");
        this.setState({ isProcessingPayment: false });
        return;
      }

      // Analytics: user has provided valid payment information. Fires BEFORE
      // the backend call so the funnel step is captured even if /createOrder
      // is rejected. GA4-standard funnel position: between begin_checkout
      // and purchase.
      this.fireAddPaymentInfoSafe();

      const { total } = this.getCartTotals();
      const cartIds = cartItems.map(function (item) { return item.id; });
      const idname = this.state.profileInfo ? this.state.profileInfo.email : '';
      const data = {
        result: token,
        idname: idname,
        payment_type: 'stripe',
        amount: total,
        userCart: cartItems,
        cartIds: cartIds,
      };

      // Include payment interval for subscription detection on the backend
      if (cartItems.length === 1 && cartItems[0].payment_interval !== null && cartItems[0].payment_interval !== undefined) {
        data.paymentInterval = cartItems[0].payment_interval;
      }

      const resp = await FD.callAPI(App_data.backend_api + '/checkout_v1/createOrder', data);

      if (resp && resp.success) {
        const orderId = resp.order_id || (resp.orders && resp.orders.id);
        this.props.router.push({
          pathname: '/thankyou/' + orderId,
        });
      } else {
        // Analytics: backend rejected the order (insufficient funds, declined,
        // server error mapped to success: false, etc.).
        const msg = (resp && resp.message) ? resp.message : "Payment failed. Please try again.";
        this.firePaymentFailedSafe('backend_rejection', msg);
        showFailure(msg);
        this.setState({ isProcessingPayment: false });
      }
    } catch (err) {
      // Analytics: uncaught exception — code bug or network failure.
      this.firePaymentFailedSafe('exception', (err && err.message) || 'unknown exception');
      showFailure("An error occurred during payment. Please try again.");
      this.setState({ isProcessingPayment: false });
    }
  }

  async getIsUserAuthorized() {
    const profileInfo = await userStore.getUserProfile();
    if (profileInfo != null) {
      this.setState({ isUserAuthorized: true, profileInfo: profileInfo });
    } else {
      this.setState({ isUserAuthorized: false, profileInfo: null });
    }
  }

  async loadUserCheckout() {
    this.setState({ isLoading: true, errorMessage: "" });

    FD.callAPI(App_data.backend_api + "/checkout_v1/getUserCart")
      .then((resp) => {
        if (resp && resp.success) {
          const cartItems = resp.user_cart || [];
          // PayPal is one-time only in #273 — if the cart contains a
          // subscription item, force the selection back to card so the
          // user doesn't sit on the "click PayPal button" hint with no
          // PayPal button rendered.
          const nextPaymentMethod =
            this.state.paymentMethod === 'paypal' && cartItems.some(isSubscriptionItem)
              ? 'card'
              : this.state.paymentMethod;
          this.setState({
            isLoading: false,
            errorMessage: "",
            cartItems,
            paymentMethod: nextPaymentMethod,
          });
        } else {
          const msg = (resp && resp.message) ? resp.message : "Failed to load user checkout";
          this.setState({
            isLoading: false,
            errorMessage: msg,
          });
          showInformation(msg);
        }
        return resp;
      })
      .catch((err) => {
        this.setState({
          isLoading: false,
          errorMessage: "Error loading  user checkout",
        });
        showFailure("Error loading  user checkout");
      });
  }

  handleInputChange(e) {
    this.setState({ [e.target.name]: e.target.value });
  }

  selectPaymentMethod(method) {
    // Defence in depth: refuse PayPal selection when cart contains a
    // subscription item (PayPal one-time only in #273). The radio is
    // hidden in that state anyway, so this should be unreachable from UI.
    if (method === 'paypal' && this.cartHasSubscription()) return;
    this.setState({ paymentMethod: method });
  }

  cartHasSubscription() {
    const items = this.state.cartItems || [];
    return items.some(isSubscriptionItem);
  }

  isUpgradeItem(item) {
    return item && item.type && String(item.type).startsWith("upgrade_");
  }

  getItemBasePrice(item) {
    const extraData = item.extraData || [];
    let addonsTotal = 0;
    for (let i = 0; i < extraData.length; i++) {
      addonsTotal += extraData[i].price;
    }
    if (item.type === "unlessUnlimited") {
      return addonsTotal;
    }
    if (this.isUpgradeItem(item)) {
      const credit = Number(item.upgrade_credit) || 0;
      return Math.max(0, item.planPrice + addonsTotal - credit);
    }
    return item.planPrice + addonsTotal;
  }

  getItemDiscountAmount(item) {
    if (!item || item.type !== "main") return 0;
    const pct = item.percent_discount;
    if (!(pct > 0)) return 0;
    // Coupon applies only to the plan price, not to add-ons
    const planPrice = Number(item.planPrice) || Number(item.price) || 0;
    return (planPrice * pct) / 100;
  }

  getCartTotals() {
    const items = this.state.cartItems || [];
    let subtotal = 0;
    let discountTotal = 0;
    for (let i = 0; i < items.length; i++) {
      subtotal += this.getItemBasePrice(items[i]);
      discountTotal += this.getItemDiscountAmount(items[i]);
    }
    const total = subtotal - discountTotal;
    return { subtotal, discountTotal, total };
  }

  renderHeader() {
    const { router } = this.props;
    const { location } = router;

    return (
      <div className="ct_container_header">
        <span
          className="ct_shopping_btn "
          onClick={() => router.push("/cart")}
        >
          Shopping cart
        </span>

        <span className="ct_header_icon_arrow">→</span>

        <span
          className={
            "ct_checkout_btn " +
            (location.pathname.includes("checkout") ? "ct_is_btn_active" : "")
          }
          onClick={() => router.push("/checkout")}>
          Checkout
        </span>

        <button type="button" className="ct_continue_btn" onClick={() => router.push("/subscribe")}>
          CONTINUE SHOPPING
        </button>
      </div>
    );
  }

  renderPaymentMethod() {
    const { stripe, cartItems, profileInfo, paymentMethod, paypal_client_id, paypal_env } = this.state;
    // Stripe's elements({ amount }) requires amount > 0. The cart total loads
    // asynchronously and may not be ready when `stripe` first becomes available,
    // so gate the wallet render on a positive amount to avoid an IntegrationError.
    const walletAmountCents = Math.round((this.getCartTotals().total || 0) * 100);

    return (
      <div className="chk_card">
        <h2 className="chk_card_title">Payment Method</h2>

        <div
          className={"chk_payment_option" + (paymentMethod === "card" ? " chk_payment_option_active" : "")}
          onClick={() => this.selectPaymentMethod("card")}
        >
          <div className="chk_payment_option_left">
            <input
              type="radio"
              name="paymentMethod"
              aria-label="Credit or debit card"
              checked={paymentMethod === "card"}
              readOnly
            />
            <i className="chk_payment_icon chk_icon_card"></i>
            <span className="chk_payment_option_label">Credit / Debit Card</span>
          </div>
          <div className="chk_payment_badges">
            <span className="chk_badge chk_badge_visa">VISA</span>
            <span className="chk_badge chk_badge_mc">MC</span>
            <span className="chk_badge chk_badge_amex">AMEX</span>
          </div>
        </div>

        {paymentMethod === "card" && (
          <StripeCheckoutForm onRef={this.onStripeFormRef} />
        )}

        {/* <div
          className={"chk_payment_option" + (paymentMethod === "applepay" ? " chk_payment_option_active" : "")}
          onClick={() => this.selectPaymentMethod("applepay")}
        >
          <div className="chk_payment_option_left">
            <input type="radio" name="paymentMethod" checked={paymentMethod === "applepay"} readOnly />
            <i className="chk_payment_icon chk_icon_wallet"></i>
            <span className="chk_payment_option_label">Apple Pay / Google Pay</span>
          </div>
          <div className="chk_payment_badges">
            <span className="chk_badge chk_badge_pay">Pay</span>
          </div>
        </div> */}

        {/* PayPal option — wired in ticket #273. PayPal Pay Later messaging
            surfaces automatically when buyers are eligible, so a separate
            "Pay Later" option is not rendered.
            Hidden when the cart contains a subscription item: our PayPal
            integration is one-time only (Orders v2). Monthly products must
            go through Stripe until PayPal Subscriptions are implemented. */}
        {paypal_client_id && !this.cartHasSubscription() && (
          <div
            className={"chk_payment_option" + (paymentMethod === "paypal" ? " chk_payment_option_active" : "")}
            onClick={() => this.selectPaymentMethod("paypal")}
          >
            <div className="chk_payment_option_left">
              <input
                type="radio"
                name="paymentMethod"
                aria-label="PayPal"
                checked={paymentMethod === "paypal"}
                readOnly
              />
              <i className="chk_payment_icon chk_icon_paypal"></i>
              <span className="chk_payment_option_label">PayPal</span>
            </div>
            <div className="chk_payment_badges">
              <span className="chk_badge chk_badge_paypal">PayPal</span>
            </div>
          </div>
        )}

        {paymentMethod === "paypal" && !this.cartHasSubscription() && (
          <PayPalButton
            paypalClientId={paypal_client_id}
            paypalEnv={paypal_env}
            getCart={() => {
              const { total } = this.getCartTotals();
              return {
                userCart: cartItems || [],
                amount: total,
                idname: profileInfo ? profileInfo.email : '',
              };
            }}
            onSuccess={(data) => {
              if (data && data.order_id) {
                this.props.router.push({ pathname: '/thankyou/' + data.order_id });
              }
            }}
            onError={(msg) => {
              this.firePaymentFailedSafe('paypal_error', msg);
              showFailure(msg || 'PayPal payment failed.');
            }}
            onCancel={() => {
              showInformation('PayPal payment cancelled.');
            }}
          />
        )}

        {stripe && walletAmountCents > 0 ? (
          <ExpressCheckoutWallets
            stripeInstance={stripe}
            amount={walletAmountCents}
            currency="usd"
            userCart={cartItems || []}
            paymentInterval={(cartItems && cartItems[0] && cartItems[0].payment_interval) || null}
            customerEmail={(profileInfo && profileInfo.email) || ''}
            apiBaseUrl={App_data.backend_api}
            authHeader={(function () { try { var s = JSON.parse(localStorage.getItem('localstorageval')); return (s && s.token) || ''; } catch (e) { return ''; } })()}
            onSuccess={(orderId) => {
              this.props.router.push({ pathname: '/thankyou/' + orderId });
            }}
            onError={(err) => {
              this.firePaymentFailedSafe('wallet_error', (err && err.message) || 'Wallet payment failed');
            }}
          />
        ) : null}

      </div>
    );
  }

  renderOrderSummary() {
    const { cartItems, paymentMethod, isProcessingPayment } = this.state;
    const { subtotal, discountTotal, total } = this.getCartTotals();

    return (
      <div className="chk_card">
        <h2 className="chk_card_title">Order Summary</h2>

        <div className="chk_summary_items">
          {(cartItems || []).map((item) => {
            const isUpgrade = this.isUpgradeItem(item);
            const isUpgradeUnlimited = isUpgrade && item.type === 'upgrade_unlimited';

            return (
              <div key={item.id}>
                {item.type !== "unlessUnlimited" && (
                  <div className="chk_summary_line">
                    <span className="chk_summary_line_name">
                      {isUpgrade
                        ? item.examName + " – Upgrade to " + item.package_type
                        : item.examName + " - " + item.package_type}
                      {isUpgrade && <span className={
                        "ct_addon_badge " +
                        (isUpgradeUnlimited ? "ct_upgrade_badge_green" : "ct_upgrade_badge")
                      }
                        style={{ fontSize: 10 }}
                      >
                        UPGRADE
                      </span>}
                    </span>
                    <span className="chk_summary_line_price">
                      {item.originalPrice && Number(item.originalPrice) !== Number(item.planPrice) && (
                        <span className="chk_summary_line_price_original">{formatMoney(item.originalPrice)}</span>
                      )}
                      {formatMoney(item.planPrice)}
                      {item.payment_interval ? <span className="ct_item_price_interval">/{item.payment_interval} </span> : ""}
                    </span>
                  </div>
                )}
                {isUpgrade && Number(item.upgrade_credit) > 0 && (
                  <div className={
                    "chk_summary_line " +
                    (isUpgradeUnlimited ? "chk_summary_line_credit_green" : "chk_summary_line_credit")
                  }>
                    <span className="chk_summary_line_name">Upgrade credit ({item.upgradeFromPackageType || "owned package"})</span>
                    <span className="chk_summary_line_price">-{formatMoney(Number(item.upgrade_credit) || 0)}</span>
                  </div>
                )}
                {this.getItemDiscountAmount(item) > 0 && (
                  <div className="chk_summary_line chk_summary_line_coupon">
                    <span className="chk_summary_line_name">Coupon applied (-{item.percent_discount}%)</span>
                    <span className="chk_summary_line_price">-{formatMoney(this.getItemDiscountAmount(item))}</span>
                  </div>
                )}
                {(item.extraData || []).map((addon) => (
                  <div className="chk_summary_line" key={addon.id}>
                    <span className="chk_summary_line_name">{addon.title}</span>
                    <span className={addon.inherited ? "chk_summary_line_included" : "chk_summary_line_price"}>
                      {addon.inherited ? "included" : formatMoney(addon.price)}
                    </span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        <div className="chk_summary_divider"></div>

        <div className="chk_summary_row">
          <span>Subtotal</span>
          <span className="chk_summary_row_sum">{formatMoney(subtotal)}</span>
        </div>

        {discountTotal > 0 && (
          <div className="chk_summary_row">
            <span>Discount</span>
            <span className="chk_summary_row_sum">-{formatMoney(discountTotal)}</span>
          </div>
        )}

        <div className="chk_summary_row">
          <span>Tax</span>
          <span className="chk_summary_row_sum">$0.00</span>
        </div>

        <div className="chk_summary_row chk_summary_row_total">
          <span>Total</span>
          <span>{formatMoney(total)}</span>
        </div>

        {paymentMethod === "paypal" && !this.cartHasSubscription() ? (
          // PayPal renders its own button inside <PayPalButton/>. We hide the
          // generic Pay button to avoid double-submit UX. The PayPal button
          // sits in the Payment Method card to the left.
          <div className="chk_pay_paypal_hint">
            Click the PayPal button in the Payment Method section to continue.
          </div>
        ) : (
          <button
            className="chk_pay_btn"
            type="button"
            onClick={this.handlePayment}
            disabled={isProcessingPayment}
          >
            {isProcessingPayment ? "Processing..." : "Pay " + formatMoney(total)}
          </button>
        )}

        <div className="chk_secure_text">
          {paymentMethod === "paypal" && !this.cartHasSubscription()
            ? "Powered by PayPal. Your payment is secure and encrypted."
            : "Powered by Stripe. Your payment is secure and encrypted."}
        </div>
      </div>
    );
  }

  render() {
    const { isLoading, errorMessage, stripe, cartItems, stripe_key } = this.state;

    const mixBlock = hasSubscriptionRegularMix(cartItems) ? (
      <div className="chk_mix_warning">
        <div className="chk_mix_warning_title">
          Subscription and regular products cannot be purchased together
        </div>
        <div className="chk_mix_warning_text">
          Your cart contains a subscription package (e.g. GMAT Focus) together with a regular
          product. Please return to the cart and check out the subscription separately.
        </div>
        <button
          type="button"
          className="chk_mix_warning_btn"
          onClick={() => this.props.router.push("/cart")}
        >
          Back to cart
        </button>
      </div>
    ) : null;

    const checkoutContent = (
      <div>
        {this.renderHeader()}

        {mixBlock ? (
          mixBlock
        ) : (
          <div className="chk_layout">
            <div className="chk_col_left">
              {this.renderPaymentMethod()}
            </div>
            <div className="chk_col_right">
              {this.renderOrderSummary()}
            </div>
          </div>
        )}
      </div>
    );

    return (
      <div className='animated fadeIn'>
        {isLoading ?
          <Loader /> :
          <div className="sb_page_container">
            {errorMessage ? (
              <div className="sb_error_message">{errorMessage}</div>
            ) : stripe ? (
              <StripeProvider key={stripe_key || 'stripe'} stripe={stripe}>
                <Elements>
                  {checkoutContent}
                </Elements>
              </StripeProvider>
            ) : (
              <Loader />
            )}
          </div>
        }
      </div>
    );
  }
}

export default Checkout;



// WEBPACK FOOTER //
// ./src/views/Cart/Checkout.js